import React from 'react';
import { ui } from '../../config/uiSizes';
import Spinner from '../migratorDashboard/Spinner';
import { getMigrationPreview } from '../../services/Migration/getMigrationPreviewService';
import { toFriendlyName } from '../../utils/formatters';
import { getTypeLabelByApiName } from '../../utils/resourceTypes';
import type { MigrationPreviewReport, PreviewWorkItem } from '../../types/migrationPreview';

interface MigrationPreviewPanelProps {
    /** Bearer token for the API call. */
    token: string;
    /** Migration whose dry-run preview should be shown. */
    migrationId: string;
    /** Optional extra classes for the outer container. */
    className?: string;
}

/** Friendly, human display name for a raw `genesyscloud_*` resource type. */
function friendlyType(rawType: string): string {
    return getTypeLabelByApiName(toFriendlyName(rawType));
}

/** Colour classes for the confidence figure, keyed off how many user-selected resources are clean. */
function confidenceColour(percent: number): string {
    if (percent >= 90) return 'text-green-600 dark:text-green-400';
    if (percent >= 60) return 'text-yellow-600 dark:text-yellow-300';
    return 'text-red-600 dark:text-red-400';
}

/** A single remediation work-list row. */
const WorkItemRow: React.FC<{ item: PreviewWorkItem }> = ({ item }) => (
    <li
        className={`rounded-lg border ${ui.page.smPadding} ${item.isHardLimit
                ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20'
                : 'border-yellow-300 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-700/20'
            }`}
    >
        <div className={`flex items-center flex-wrap ${ui.gap.sm} ${ui.mb.sm}`}>
            <span className={`${ui.text.body} font-semibold text-gray-900 dark:text-gray-100 break-words`}>
                {friendlyType(item.resourceType)}: {item.resourceName}
            </span>
            <span
                className={`${ui.badge.sm} rounded-full font-medium ${item.isHardLimit
                        ? 'bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-100'
                        : 'bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-100'
                    }`}
            >
                {item.causeLabel}
            </span>
            {item.isHardLimit ? (
                <span className={`${ui.badge.sm} rounded-full font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200`}>
                    Platform limit — cannot be fixed in-tool
                </span>
            ) : (
                <span className={`${ui.badge.sm} rounded-full font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200`}>
                    Fixable
                </span>
            )}
            {!item.isUserSelected && (
                <span className={`${ui.badge.sm} rounded-full font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400`}>
                    Auto-added dependency
                </span>
            )}
        </div>

        {item.reason && (
            <p className={`${ui.text.xs} text-gray-600 dark:text-gray-400 ${ui.mb.sm} break-words`}>
                {item.reason}
            </p>
        )}

        {item.recommendedFix && (
            <p className={`${ui.text.xs} text-gray-800 dark:text-gray-200 break-words`}>
                <span className="font-semibold">Fix:</span> {item.recommendedFix}
            </p>
        )}
    </li>
);

/**
 * Renders the dry-run migration preview: the remediation work-list is the primary artifact and the
 * confidence percentage is secondary. Every work item shows a plain-language cause and a concrete
 * fix, and platform hard-limits are flagged so the user understands when 100% is unreachable and
 * why. A "Re-run preview" control re-fetches the report to support the fix → re-preview loop (after
 * the user applies a fix and re-runs the dry-run, refreshing shows the improved confidence).
 *
 * Renders a friendly empty state when no preview has been generated yet (the endpoint returns 404).
 */
const MigrationPreviewPanel: React.FC<MigrationPreviewPanelProps> = ({ token, migrationId, className = '' }) => {
    const [report, setReport] = React.useState<MigrationPreviewReport | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);

    const load = React.useCallback(async () => {
        if (!token || !migrationId) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const result = await getMigrationPreview(token, migrationId);
            setReport(result);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load the migration preview.');
        } finally {
            setLoading(false);
        }
    }, [token, migrationId]);

    React.useEffect(() => {
        void load();
    }, [load]);

    if (loading) {
        return (
            <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg ${ui.page.cardPadding} ${className}`}>
                <Spinner />
            </div>
        );
    }

    return (
        <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg ${ui.page.cardPadding} ${className}`}>
            <div className={`flex items-center justify-between flex-wrap ${ui.gap.sm} ${ui.mb.section}`}>
                <h3 className={`${ui.text.sectionTitle} font-bold text-gray-900 dark:text-gray-100`}>
                    Migration preview
                </h3>
                <button
                    type="button"
                    onClick={() => void load()}
                    className={`rounded-lg font-semibold ${ui.button.sm} bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                >
                    Re-run preview
                </button>
            </div>

            {error && (
                <div
                    className={`bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg ${ui.page.sectionPadding} ${ui.text.xs} text-red-700 dark:text-red-300 break-words`}
                    role="alert"
                >
                    {error}
                </div>
            )}

            {!error && report === null && (
                <p className={`${ui.text.body} text-gray-600 dark:text-gray-400 break-words`}>
                    No preview has been generated for this migration yet. Run a dry-run import first, then re-run
                    the preview to see the remediation work-list and confidence.
                </p>
            )}

            {!error && report !== null && report.nothingToApply && (
                <div
                    className={`bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700 rounded-lg ${ui.page.sectionPadding}`}
                    role="status"
                >
                    <p className={`${ui.text.sectionTitle} font-bold text-blue-800 dark:text-blue-200 ${ui.mb.sm}`}>
                        No changes needed
                    </p>
                    <p className={`${ui.text.body} text-blue-900 dark:text-blue-100 break-words`}>
                        {report.alreadyPresentCount > 0
                            ? `All ${report.alreadyPresentCount} resource(s) you selected already exist in the target org and match — a real import would adopt them and change nothing.`
                            : 'This preview found no resources to create, update, or delete. Check that the export contains the resources you expected, then re-run the preview.'}
                    </p>
                </div>
            )}

            {!error && report !== null && !report.nothingToApply && (
                <>
                    {/* Confidence + summary counts */}
                    <div className={`flex items-end flex-wrap ${ui.gap.lg} ${ui.mb.section}`}>
                        <div>
                            <span className={`${ui.text.confidenceValue} font-extrabold ${confidenceColour(report.confidencePercent)}`}>
                                {report.confidencePercent}%
                            </span>
                            <p className={`${ui.text.xs} text-gray-500 dark:text-gray-400`}>
                                confidence ({report.willApplyCleanlyCount} of {report.selectedResourceCount} selected resources will apply cleanly)
                            </p>
                        </div>
                        <div className={`flex ${ui.gap.md}`}>
                            <div>
                                <span className={`${ui.text.sectionTitle} font-bold text-yellow-700 dark:text-yellow-300`}>
                                    {report.fixableCount}
                                </span>
                                <p className={`${ui.text.xxs} text-gray-500 dark:text-gray-400`}>fixable</p>
                            </div>
                            <div>
                                <span className={`${ui.text.sectionTitle} font-bold text-red-600 dark:text-red-400`}>
                                    {report.hardLimitCount}
                                </span>
                                <p className={`${ui.text.xxs} text-gray-500 dark:text-gray-400`}>platform limits</p>
                            </div>
                        </div>
                    </div>

                    {/* Confidence bar */}
                    <div className={`w-full ${ui.widthAndHeight.progressBar} rounded-full bg-gray-200 dark:bg-gray-700 ${ui.mb.section} overflow-hidden`}>
                        <div
                            className={`h-full rounded-full ${report.confidencePercent >= 90
                                    ? 'bg-green-500'
                                    : report.confidencePercent >= 60
                                        ? 'bg-yellow-500'
                                        : 'bg-red-500'
                                }`}
                            style={{ width: `${report.confidencePercent}%` }}
                        />
                    </div>

                    {/* Work-list */}
                    {report.workItems.length === 0 ? (
                        <p className={`${ui.text.body} text-green-700 dark:text-green-400 break-words`}>
                            Everything you selected is planned to apply cleanly — no remediation needed.
                        </p>
                    ) : (
                        <>
                            <p className={`${ui.text.label} font-semibold text-gray-800 dark:text-gray-200 ${ui.mb.sm}`}>
                                Remediation work-list ({report.workItems.length})
                            </p>
                            <ul className={`flex flex-col ${ui.gap.sm}`}>
                                {report.workItems.map((item) => (
                                    <WorkItemRow key={item.address} item={item} />
                                ))}
                            </ul>
                            <p className={`${ui.text.xxs} text-gray-500 dark:text-gray-400 mt-3 break-words`}>
                                Apply the fixes above (fixable items first — they raise the confidence), re-run the
                                dry-run, then use "Re-run preview" to see the updated result. Platform limits cannot be
                                fixed in-tool and mean 100% may be unreachable for this selection.
                            </p>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default MigrationPreviewPanel;
